/**
 * canvas_flow_layout.jsx
 *
 * fill / fill_disable 模式的文件流排版渲染器。
 *
 * 策略：
 *   1. 按各裝置的 y 座標排序元件（忠實還原設計時的行序）
 *   2. y 差距 ≤ threshold 視為同一「邏輯行」，同行內按 x 座標左到右排
 *   3. 每個欄位的寬度用 width/canvasWidth 換算成 flex-basis %
 *   4. flex-wrap 讓容器不夠寬時自動折行，不強制壓縮
 *   5. 每個欄位有 minWidth 下限，switch/agreement 保護 label 不截斷
 */

import React, { useMemo } from "react"

const SKIP_TYPES = new Set([
    "flow_line", "straight-line", "start_node", "end_node",
    "process_node", "decision_node"
])

// 欄位最小寬度（px）—— 視 type 而定
const MIN_WIDTHS = {
    switch: 130,
    agreement: 130,
    default: 90,
}

function resolveMode(canvasMode, canvasWidth) {
    if (canvasMode !== "auto") return canvasMode
    // auto 模式：根據 canvasWidth 判斷對應裝置
    if (canvasWidth >= 1000) return "desktop"
    if (canvasWidth >= 600) return "tablet"
    return "mobile"
}

function getMinWidth(type) {
    return MIN_WIDTHS[type] ?? MIN_WIDTHS.default
}

function getCoord(val, mode) {
    if (val == null) return 0
    if (typeof val === "object") return Number(val[mode] ?? val.desktop ?? 0)
    return Number(val)
}

function getWidth(el, mode, canvasWidth) {
    const wObj = (typeof el?.width === "object" && el?.width !== null) ? el.width
        : (typeof el?.style?.width === "object" && el?.style?.width !== null) ? el.style.width
        : null
    if (wObj) {
        // 新格式：用對應裝置的值
        const elW = Number(wObj[mode] ?? wObj.desktop ?? 0)
        return canvasWidth > 0 && elW > 0
            ? Math.max(0.08, Math.min(1, elW / canvasWidth))
            : 0
    } else {
        // 舊格式：用桌面版比例
        const elW = Number(el?.style?.width ?? el?.width ?? 0)
        return elW > 0 ? Math.max(0.08, Math.min(1, elW / 1200)) : 0
    }
}

const CanvasFlowLayout = ({
    elements = [],
    canvasMode = "desktop",
    canvasWidth = 1200,
    mode = "fill",
    renderElement,
    className = "",
}) => {
    // auto 模式解析成具體裝置名稱
    const resolvedMode = resolveMode(canvasMode, canvasWidth)
    // ── 1. 過濾 + 按 y（主）、x（次）排序 ───────────────────────────────
    const sorted = useMemo(() => {
        return elements
            .filter(el => {
                if (el?.hidden === 1) return false
                if (SKIP_TYPES.has(el?.type)) return false
                if (el?.parentId) return false
                // fill_hidden：後台設定「前台隱藏」，fill/fill_disable 模式不渲染
                const fillHidden = el?.form_form_column_data?.[0]?.fill_hidden
                if (fillHidden == 1 || fillHidden === true) return false
                return true
            })
            .sort((a, b) => {
                const ay = getCoord(a.y, resolvedMode)
                const by = getCoord(b.y, resolvedMode)
                if (Math.abs(ay - by) > 1) return ay - by
                return getCoord(a.x, resolvedMode) - getCoord(b.x, resolvedMode)
            })
    }, [elements, resolvedMode])

    // ── 2. 分組同行（y 差距 ≤ threshold）────────────────────────────────
    const rows = useMemo(() => {
        // threshold：取各裝置常見行高的一半；桌面 ~60，手機行高較小用 ~50
        const threshold = resolvedMode === "mobile" ? 40 : 55

        const result = []
        let row = []
        let rowY = null

        sorted.forEach(el => {
            const y = getCoord(el.y, resolvedMode)
            if (rowY === null || Math.abs(y - rowY) <= threshold) {
                row.push(el)
                if (rowY === null) rowY = y
            } else {
                // 同行按 x 排序後推入
                result.push(row.slice().sort((a, b) =>
                    getCoord(a.x, resolvedMode) - getCoord(b.x, resolvedMode)
                ))
                row = [el]
                rowY = y
            }
        })
        if (row.length) {
            result.push(row.slice().sort((a, b) =>
                getCoord(a.x, resolvedMode) - getCoord(b.x, resolvedMode)
            ))
        }
        return result
    }, [sorted, resolvedMode])

    // ── DEBUG：輸出每個 element 的 y/width 實際值（確認後可移除）────────────
    if (typeof window !== 'undefined' && window.__canvasDebug) {
        console.group(`[CanvasFlowLayout] mode=${canvasMode} canvasWidth=${canvasWidth}`)
        sorted.forEach(el => {
            const y = getCoord(el.y, resolvedMode)
            const wObj = (typeof el?.width === "object" && el?.width !== null) ? el.width
                : (typeof el?.style?.width === "object" && el?.style?.width !== null) ? el.style.width
                : null
            const elW = wObj ? Number(wObj[resolvedMode] ?? wObj.desktop ?? 0) : Number(el?.style?.width ?? el?.width ?? 0)
            const fmt = wObj ? '新格式' : '舊格式'
            const ratio = elW / (wObj ? canvasWidth : 1200)
            console.log(`  "${el?.form_form_column_data?.[0]?.code || el.type}" y=${y} width=${elW}(${fmt}) ratio=${ratio.toFixed(2)}`)
        })
        rows.forEach((row, i) => {
            console.log(`  row[${i}]:`, row.map(e => e?.form_form_column_data?.[0]?.code || e.type))
        })
        console.groupEnd()
    }
    // ── 在 console 輸入 window.__canvasDebug=true 後重新渲染即可看到輸出 ──

    return (
        <div
            className={[
                "canvas-flow-layout w-full",
                mode === "fill_disable" ? "pointer-events-none select-none" : "",
                className,
            ].filter(Boolean).join(" ")}
        >
            {rows.map((row, rowIdx) => {
                const isLast = rowIdx === rows.length - 1
                const rowBorder = isLast ? 'none' : '1px solid #f0f0f0'

                // ── 單欄行 ───────────────────────────────────────────────
                if (row.length === 1) {
                    return (
                        <div key={row[0].id} style={{ borderBottom: rowBorder }}>
                            {renderElement(row[0])}
                        </div>
                    )
                }

                // ── 多欄行：用 flex-wrap + flex-basis % ──────────────────
                // 計算每個欄位的設計寬度比例
                const DESKTOP_CANVAS_WIDTH = 1200
                const items = row.map(el => {
                    const wObj = (typeof el?.width === "object" && el?.width !== null) ? el.width
                        : (typeof el?.style?.width === "object" && el?.style?.width !== null) ? el.style.width
                        : null
                    let ratio
                    if (wObj) {
                        const elW = Number(wObj[resolvedMode] ?? wObj.desktop ?? 0)
                        ratio = canvasWidth > 0 && elW > 0
                            ? Math.max(0.08, Math.min(1, elW / canvasWidth))
                            : 1 / row.length
                    } else {
                        const elW = Number(el?.style?.width ?? el?.width ?? 0)
                        ratio = DESKTOP_CANVAS_WIDTH > 0 && elW > 0
                            ? Math.max(0.08, Math.min(1, elW / DESKTOP_CANVAS_WIDTH))
                            : 1 / row.length
                    }
                    return { el, ratio }
                })

                // 如果同行 ratio 總和 > 1，依照 x 座標重新分成多個 sub-row
                // 讓每個 sub-row 的 ratio 加總 ≤ 1
                const subRows = []
                let curSubRow = []
                let curTotal = 0
                items.forEach(item => {
                    if (curTotal + item.ratio > 1.05 && curSubRow.length > 0) {
                        subRows.push(curSubRow)
                        curSubRow = [item]
                        curTotal = item.ratio
                    } else {
                        curSubRow.push(item)
                        curTotal += item.ratio
                    }
                })
                if (curSubRow.length) subRows.push(curSubRow)

                return subRows.map((subRow, subIdx) => {
                    const isLastSubRow = subIdx === subRows.length - 1
                    const subRowBorder = (!isLastSubRow || !isLast) ? '1px solid #f0f0f0' : 'none'
                    const subTotal = subRow.reduce((s, i) => s + i.ratio, 0)
                    const subScale = subTotal < 0.98 ? 1 / subTotal : 1

                    return (
                        <div
                            key={`row-${rowIdx}-sub-${subIdx}`}
                            style={{ display: 'flex', flexWrap: 'nowrap', borderBottom: subRowBorder }}
                        >
                            {subRow.map(({ el, ratio }, elIdx) => {
                                const basisPct = Math.min(100, ratio * subScale * 100).toFixed(2)
                                const minW = getMinWidth(el?.type)
                                const isLastItem = elIdx === subRow.length - 1
                                return (
                                    <div
                                        key={el.id}
                                        style={{
                                            flexBasis: `${basisPct}%`,
                                            flexGrow: isLastItem ? 1 : 0,
                                            flexShrink: 1,
                                            minWidth: `${minW}px`,
                                            maxWidth: isLastItem ? '100%' : `${basisPct}%`,
                                            borderRight: isLastItem ? 'none' : '1px solid #f0f0f0',
                                            boxSizing: 'border-box',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {renderElement(el)}
                                    </div>
                                )
                            })}
                        </div>
                    )
                })
            })}
        </div>
    )
}

export default CanvasFlowLayout
